import { Bus, ClipboardList, FileText, UserCog, Wrench } from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { cn } from '@/lib/utils';
import type { UpcomingService, VehicleSummary } from '@/types/transport';
import { daysUntil, toDateLabel } from '../constants';
import { QuickActionGrid, RailCard, type QuickAction } from './TransportSideRail';

export interface VehiclesSideRailProps {
  summary: VehicleSummary;
  services: UpcomingService[];
  actions: QuickAction[];
  onViewStatusReport?: () => void;
  onViewComplianceReport?: () => void;
  onViewAllServices?: () => void;
}

/**
 * The Vehicles tab's rail: how the fleet divides, whether its papers are in
 * order, and what is due in the workshop.
 *
 * Two rings rather than one, because they answer different questions of the
 * same fleet — where the vehicles are today, and whether they are legal to be
 * there at all. A single ring would have to drop one of them.
 */
export function VehiclesSideRail({
  summary,
  services,
  actions,
  onViewStatusReport,
  onViewComplianceReport,
  onViewAllServices,
}: VehiclesSideRailProps) {
  const statusSlices: DonutSlice[] = [
    {
      key: 'onRoute',
      label: 'On route',
      value: summary.onRoute,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'maintenance',
      label: 'Maintenance',
      value: summary.underMaintenance,
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      value: summary.inactive,
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
  ];

  const complianceSlices: DonutSlice[] = [
    {
      key: 'compliant',
      label: 'Compliant',
      value: summary.compliant,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'nonCompliant',
      label: 'Non-compliant',
      value: summary.nonCompliant,
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <RailCard
        title="Vehicle status"
        action={onViewStatusReport && { label: 'View report', onSelect: onViewStatusReport }}
      >
        <DonutChart
          slices={statusSlices}
          total={summary.totalVehicles}
          caption="Total vehicles"
          layout="responsive"
        />
      </RailCard>

      <RailCard
        title="Fitness compliance"
        action={onViewComplianceReport && { label: 'View report', onSelect: onViewComplianceReport }}
      >
        <DonutChart
          slices={complianceSlices}
          total={summary.compliant + summary.nonCompliant}
          caption="Certificates"
          layout="responsive"
        />
        <p className="mt-3 border-t border-line pt-3 text-sm font-semibold text-ink-2">
          <span className="text-lg font-[800] text-ink">{summary.fitnessCompliance}%</span> of the
          fleet holds a valid fitness certificate.
        </p>
      </RailCard>

      <RailCard
        title="Upcoming services"
        action={onViewAllServices && { label: 'View all', onSelect: onViewAllServices }}
      >
        <ul className="flex flex-col gap-1">
          {services.map((service, index) => {
            const days = daysUntil(service.dueOn);
            // Overdue is not the same as due soon, and an office reading this
            // list is deciding what to book first.
            const overdue = days < 0;
            const soon = days >= 0 && days <= 7;

            return (
              <li
                key={service.uuid}
                className="flex items-center gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-surface-2 animate-fade-up"
                style={{ animationDelay: `${Math.min(index, 5) * 50}ms` }}
              >
                <span
                  className={cn(
                    'grid h-8 w-8 shrink-0 place-items-center rounded-xl',
                    overdue ? 'bg-crit-bg text-crit' : soon ? 'bg-warn-bg text-warn' : 'bg-info-bg text-info',
                  )}
                  aria-hidden
                >
                  <Bus className="h-4 w-4" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-ink">
                    {service.registrationNumber}
                  </span>
                  <span
                    className={cn(
                      'block truncate text-xs font-semibold',
                      overdue ? 'text-crit' : soon ? 'text-warn' : 'text-ink-3',
                    )}
                  >
                    {overdue
                      ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`
                      : `Service due in ${days} day${days === 1 ? '' : 's'}`}
                  </span>
                </span>

                <time className="shrink-0 text-xs font-bold text-ink-3">
                  {toDateLabel(service.dueOn)}
                </time>
              </li>
            );
          })}
        </ul>
      </RailCard>

      <RailCard title="Quick actions">
        <QuickActionGrid actions={actions} />
      </RailCard>
    </div>
  );
}

/** The four things the office does to a vehicle. */
export const VEHICLE_ACTION_ICONS = {
  addVehicle: Bus,
  assignDriver: UserCog,
  vehicleReport: FileText,
  maintenanceLog: Wrench,
  serviceSchedule: ClipboardList,
};
